import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

function envUrl(envVar, fallback) {
  return (envVar && envVar !== 'undefined') ? envVar : fallback;
}

const PRODUCTS = {
  crime_central:  { name: 'Crime Central',  url: () => envUrl(process.env.REACT_APP_CC_URL, 'https://app.crime-central.com') },
  policy_central: { name: 'Policy Central', url: () => envUrl(process.env.REACT_APP_PC_URL, 'https://policy.crime-central.com') },
};

const CURRENT_APP = 'policy_central';

export default function AppSwitcher({ enabledModules, orgModules }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { idToken, refreshToken, platformDept } = useAuth();

  const userModules = enabledModules
    ? [...new Set(enabledModules.split(',').map(m => m.trim()).filter(m => m && PRODUCTS[m] && m !== CURRENT_APP))]
    : [];
  const allowedByOrg = orgModules
    ? new Set(orgModules.split(',').map(m => m.trim()))
    : null;
  const modules = allowedByOrg ? userModules.filter(m => allowedByOrg.has(m)) : userModules;

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (modules.length === 0) return null;

  function buildHash() {
    const parts = [`platform_id_token=${encodeURIComponent(idToken)}`];
    if (refreshToken) parts.push(`platform_refresh_token=${encodeURIComponent(refreshToken)}`);
    if (platformDept) parts.push(`platform_dept=${encodeURIComponent(platformDept)}`);
    return parts.join('&');
  }

  function openApp(module) {
    const base = PRODUCTS[module].url();
    // Same-tab navigation: the target app's own httpOnly-cookie session restore
    // reads these hash params to seed its own cookie, then discards them from
    // memory — no need for a separate tab/window anymore.
    // Matches cc-app-web's outbound switch pattern for cross-product consistency;
    // plaintext hash tokens are a known, accepted gap tracked in CC-536.
    window.location.href = idToken ? `${base}#${buildHash()}` : base;
  }

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-label="Switch app"
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none',
          border: 'none',
          padding: '6px',
          cursor: 'pointer',
          color: 'inherit',
          display: 'flex',
          alignItems: 'center',
          borderRadius: 4,
          opacity: 0.7,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="2" cy="2" r="1.5"/>
          <circle cx="8" cy="2" r="1.5"/>
          <circle cx="14" cy="2" r="1.5"/>
          <circle cx="2" cy="8" r="1.5"/>
          <circle cx="8" cy="8" r="1.5"/>
          <circle cx="14" cy="8" r="1.5"/>
          <circle cx="2" cy="14" r="1.5"/>
          <circle cx="8" cy="14" r="1.5"/>
          <circle cx="14" cy="14" r="1.5"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          minWidth: 180,
          zIndex: 1000,
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: 6,
          padding: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <div style={{
            fontSize: 11,
            color: '#718096',
            padding: '2px 8px 6px',
            borderBottom: '1px solid #e2e8f0',
            marginBottom: 4,
          }}>
            Switch product
          </div>
          {modules.map(m => (
            <button
              key={m}
              type="button"
              onClick={() => openApp(m)}
              style={{
                display: 'block',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#2d3748',
                padding: '5px 8px',
                borderRadius: 4,
                fontSize: 13,
                width: '100%',
                textAlign: 'left',
              }}
            >
              {PRODUCTS[m].name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
